/**
 * Countries and their regions, with lookups between ISO codes and display
 * names. Profile addresses store names; order shipping addresses send codes.
 */

import countryData from "./country-data.json";

interface Region {
  name: string;
  shortCode: string;
}

interface Country {
  countryName: string;
  countryShortCode: string;
  regions: Region[];
}

const data = countryData as Country[];

export function getCountries(): { name: string; shortCode: string }[] {
  return data.map((c) => ({
    name: c.countryName,
    shortCode: c.countryShortCode,
  }));
}

export function getRegions(
  countryName: string,
): { name: string; shortCode: string }[] {
  const country = data.find(
    (c) => c.countryName.toLowerCase() === countryName.toLowerCase(),
  );
  return country?.regions ?? [];
}

// Resolve a country short code (e.g. "US") to full name (e.g. "United States")
export function resolveCountryName(nameOrCode: string): string | null {
  const byName = data.find(
    (c) => c.countryName.toLowerCase() === nameOrCode.toLowerCase(),
  );
  if (byName) return byName.countryName;

  const byCode = data.find(
    (c) => c.countryShortCode.toLowerCase() === nameOrCode.toLowerCase(),
  );
  return byCode?.countryName ?? null;
}

// Resolve a region short code to full name within a country
export function resolveRegionName(
  countryName: string,
  nameOrCode: string,
): string | null {
  const regions = getRegions(countryName);
  const byName = regions.find(
    (r) => r.name.toLowerCase() === nameOrCode.toLowerCase(),
  );
  if (byName) return byName.name;

  const byCode = regions.find(
    (r) => r.shortCode.toLowerCase() === nameOrCode.toLowerCase(),
  );
  return byCode?.name ?? null;
}
